import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { ArrowRight, Check } from 'lucide-react';
import SEO from '../SEO';
import { useModalContext } from '../../App';

export interface SolutionContent {
  slug: string;
  label: string;
  seoTitle: string;
  seoDescription: string;
  seoKeywords: string;
  eyebrow: string;
  headline: string;
  highlight: string;
  lede: string;
  bullets: string[];
  blocks: { title: string; body: string }[];
  steps: { title: string; body: string }[];
  fit: string[];
}

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.5, ease: 'easeOut' as const },
};

const HeroSection = styled.section`
  padding: 5.5rem 0 4.5rem;
  background: linear-gradient(180deg, #fff7f0 0%, #ffffff 100%);
  border-bottom: 1px solid var(--border-color);
`;

const Breadcrumb = styled.nav`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #888;
  margin-bottom: 2rem;

  a {
    color: #888;
    transition: color 0.2s ease;

    &:hover {
      color: var(--primary-color);
    }
  }

  span {
    color: var(--primary-color);
  }
`;

const Eyebrow = styled.span`
  display: inline-block;
  font-size: 0.875rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 3px;
  color: var(--primary-color);
  margin-bottom: 1.25rem;
`;

const Headline = styled.h1`
  font-size: 3.75rem;
  font-weight: 900;
  line-height: 1.05;
  letter-spacing: -0.03em;
  text-transform: uppercase;
  color: #111;
  max-width: 820px;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Highlight = styled.span`
  color: var(--primary-color);
`;

const Lede = styled.p`
  font-size: 1.25rem;
  line-height: 1.7;
  color: #555;
  max-width: 720px;
  margin-bottom: 2.5rem;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const HeroActions = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  flex-wrap: wrap;
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  background-color: var(--primary-color);
  color: #fff;
  font-weight: 700;
  font-size: 1.05rem;
  letter-spacing: 0.5px;
  padding: 1.1rem 2.25rem;
  border-radius: 8px;
  transition: all 0.3s ease;

  &:hover {
    background-color: #e55a00;
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(255, 107, 0, 0.3);
  }

  svg {
    transition: transform 0.3s ease;
  }

  &:hover svg {
    transform: translateX(3px);
  }
`;

const SecondaryLink = styled(Link)`
  color: #555;
  font-weight: 600;
  font-size: 1rem;
  border-bottom: 2px solid var(--border-color);
  padding-bottom: 2px;
  transition: color 0.2s ease, border-color 0.2s ease;

  &:hover {
    color: var(--primary-color);
    border-color: var(--primary-color);
  }
`;

const BulletGrid = styled.ul`
  list-style: none;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.9rem 2.5rem;
  max-width: 900px;
  margin-top: 3rem;
  padding-top: 2.5rem;
  border-top: 1px solid rgba(0, 0, 0, 0.08);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }

  li {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    font-size: 1.05rem;
    font-weight: 600;
    color: #333;

    svg {
      flex-shrink: 0;
      width: 20px;
      height: 20px;
      margin-top: 3px;
      color: var(--primary-color);
    }
  }
`;

const BodySection = styled.section`
  padding: 5rem 0 0;
`;

const BlocksGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const BlockCard = styled.div`
  background: #fff;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 2.25rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    border-color: rgba(255, 107, 0, 0.35);
    box-shadow: 0 12px 35px rgba(0, 0, 0, 0.08);
  }

  h3 {
    font-size: 1.35rem;
    font-weight: 800;
    color: #111;
    margin-bottom: 0.75rem;
    letter-spacing: -0.01em;
  }

  p {
    font-size: 1.05rem;
    line-height: 1.7;
    color: #555;
    margin: 0;
  }
`;

const StepsSection = styled.section`
  padding: 5rem 0 0;
`;

const SectionTitle = styled.h2`
  font-size: 2.25rem;
  font-weight: 900;
  letter-spacing: -0.02em;
  text-transform: uppercase;
  color: #111;
  margin-bottom: 2.5rem;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const StepsList = styled.ol`
  list-style: none;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }

  li {
    border-top: 3px solid var(--primary-color);
    padding-top: 1.25rem;

    strong {
      display: block;
      font-size: 0.8rem;
      font-weight: 800;
      letter-spacing: 2px;
      color: var(--primary-color);
      margin-bottom: 0.5rem;
    }

    h3 {
      font-size: 1.15rem;
      font-weight: 800;
      color: #111;
      margin-bottom: 0.5rem;
    }

    p {
      font-size: 0.975rem;
      line-height: 1.65;
      color: #555;
      margin: 0;
    }
  }
`;

const FitSection = styled.section`
  padding: 5rem 0 0;
`;

const FitGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem 2.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }

  div {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    font-size: 1.05rem;
    color: #444;
    line-height: 1.6;

    svg {
      flex-shrink: 0;
      width: 20px;
      height: 20px;
      margin-top: 3px;
      color: var(--primary-color);
    }
  }
`;

const CTASection = styled.section`
  margin: 5.5rem 0 5rem;
  padding: 4rem 3rem;
  text-align: center;
  background: linear-gradient(135deg, rgba(255, 107, 0, 0.06), rgba(255, 143, 66, 0.02));
  border: 1px solid rgba(255, 107, 0, 0.15);
  border-radius: 16px;

  @media (max-width: 768px) {
    margin: 4rem 0;
    padding: 3rem 1.5rem;
  }

  h2 {
    font-size: 2.5rem;
    font-weight: 900;
    letter-spacing: -0.02em;
    text-transform: uppercase;
    color: #111;
    margin-bottom: 1rem;

    @media (max-width: 768px) {
      font-size: 1.9rem;
    }
  }

  p {
    font-size: 1.15rem;
    color: #555;
    max-width: 640px;
    margin: 0 auto 2.25rem;
  }
`;

const SolutionPageLayout: React.FC<{ content: SolutionContent }> = ({ content }) => {
  const { openLearnMoreModal } = useModalContext();

  return (
    <main className="font--jakarta">
      <SEO
        title={content.seoTitle}
        description={content.seoDescription}
        keywords={content.seoKeywords}
        canonicalUrl={`/solutions/${content.slug}`}
        pageType="service"
      />

      <HeroSection>
        <div className="container">
          <Breadcrumb aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span aria-hidden="true">/</span>
            <span>{content.label}</span>
          </Breadcrumb>

          <motion.div {...fadeUp}>
            <Eyebrow>{content.eyebrow}</Eyebrow>
            <Headline>
              {content.headline} <Highlight>{content.highlight}</Highlight>
            </Headline>
            <Lede>{content.lede}</Lede>
            <HeroActions>
              <PrimaryButton onClick={() => openLearnMoreModal(content.label)}>
                Start a conversation
                <ArrowRight size={18} />
              </PrimaryButton>
              <SecondaryLink to="/#process">See how we work</SecondaryLink>
            </HeroActions>
          </motion.div>

          <BulletGrid>
            {content.bullets.map((bullet) => (
              <li key={bullet}>
                <Check />
                {bullet}
              </li>
            ))}
          </BulletGrid>
        </div>
      </HeroSection>

      <BodySection>
        <div className="container">
          <BlocksGrid>
            {content.blocks.map((block) => (
              <BlockCard key={block.title}>
                <h3>{block.title}</h3>
                <p>{block.body}</p>
              </BlockCard>
            ))}
          </BlocksGrid>
        </div>
      </BodySection>

      <StepsSection>
        <div className="container">
          <SectionTitle>How it works</SectionTitle>
          <StepsList>
            {content.steps.map((step, index) => (
              <li key={step.title}>
                <strong>{String(index + 1).padStart(2, '0')}</strong>
                <h3>{step.title}</h3>
                <p>{step.body}</p>
              </li>
            ))}
          </StepsList>
        </div>
      </StepsSection>

      <FitSection>
        <div className="container">
          <SectionTitle>Who it's for</SectionTitle>
          <FitGrid>
            {content.fit.map((item) => (
              <div key={item}>
                <Check />
                {item}
              </div>
            ))}
          </FitGrid>
        </div>
      </FitSection>

      <div className="container">
        <CTASection>
          <h2>Not sure where to start?</h2>
          <p>
            Tell us what's slowing your team down. We'll map the quickest path to fixing it, and
            you'll know exactly what a project looks like before committing to anything.
          </p>
          <PrimaryButton onClick={() => openLearnMoreModal(content.label)}>
            Start a conversation
            <ArrowRight size={18} />
          </PrimaryButton>
        </CTASection>
      </div>
    </main>
  );
};

export default SolutionPageLayout;
